import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Download.module.css';
import Card from '../components/Card';
import Button from '../components/Button';
import { useSubscription, useLicenseTokens } from '../hooks';
import { getDaysRemaining } from '../utils/helpers';
import {
  DownloadIcon,
  KeyIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  ShieldIcon,
  InfoIcon,
} from '../components/Icons';

// Windows desktop app (DSC BulkSigner) package
export const DESKTOP_APP_DOWNLOAD_URL =
  'https://signulu-documents-dms.s3.us-east-1.amazonaws.com/DSCBulkSigner.zip';
const DESKTOP_APP_FILE_NAME = 'DSCBulkSigner.zip';
const DESKTOP_APP_SIZE = '~53 MB';

const STEPS = [
  {
    title: 'Download the app',
    body: `Click "Download for Windows" to get ${DESKTOP_APP_FILE_NAME} (${DESKTOP_APP_SIZE}).`,
  },
  {
    title: 'Extract and install',
    body: 'Right-click the ZIP file → "Extract All", then open the extracted folder and run the DSC BulkSigner setup / EXE. If Windows SmartScreen appears, choose "More info" → "Run anyway".',
  },
  {
    title: 'Generate a licence token',
    body: 'Go to Subscription → License Tokens → "Generate Token". Give it the name of this computer (e.g. "Office PC"), then copy or download the token.',
    action: 'token',
  },
  {
    title: 'Activate the app',
    body: 'Open DSC BulkSigner on the same computer and paste the licence token when it asks for activation. Each token activates one system.',
  },
  {
    title: 'Plug in your DSC and start signing',
    body: 'Insert your DSC USB token, select your PDF or folder, place the signature and sign.',
  },
];

const REQUIREMENTS = [
  'A Windows desktop or laptop',
  'DSC USB token with a Class 3 certificate (Individual or Organisation)',
  'The USB token driver installed on the computer',
  'An active plan or free trial on this account',
];

function Download() {
  const navigate = useNavigate();
  const { subscription, loading: subLoading } = useSubscription();
  const { usage, loading: tokensLoading } = useLicenseTokens();

  const daysLeft = subscription?.end_date ? getDaysRemaining(subscription.end_date) : 0;
  const hasActivePlan = !!subscription && daysLeft > 0;
  const planName = subscription?.is_trial
    ? 'Free Trial'
    : subscription?.plan?.name || subscription?.plan_name || 'Plan';

  const totalLicences = usage?.total_licences ?? 0;
  const usedLicences = usage?.used_licences ?? 0;
  const availableLicences = usage?.available_licences ?? Math.max(0, totalLicences - usedLicences);

  const goToTokens = () => navigate('/subscription#license-tokens');

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Download DSC BulkSigner</h1>
        <p className={styles.subtitle}>
          Install the Windows app, activate it with a licence token and start signing PDFs with your DSC.
        </p>
      </header>

      {/* Hero / download */}
      <Card padding="none" className={styles.heroCard}>
        <div className={styles.hero}>
          <div className={styles.heroIcon}>
            <DownloadIcon size={30} />
          </div>
          <div className={styles.heroText}>
            <h2 className={styles.heroTitle}>DSC BulkSigner for Windows</h2>
            <p className={styles.heroMeta}>
              {DESKTOP_APP_FILE_NAME} · {DESKTOP_APP_SIZE} · Windows desktop
            </p>
            <p className={styles.heroDesc}>
              Single and folder-based bulk PDF signing with your DSC USB token. Documents are processed
              locally on your computer.
            </p>
          </div>
          <div className={styles.heroActions}>
            <a
              href={DESKTOP_APP_DOWNLOAD_URL}
              download={DESKTOP_APP_FILE_NAME}
              className={styles.downloadButton}
            >
              <DownloadIcon size={18} />
              Download for Windows
            </a>
            <span className={styles.heroHint}>After downloading, follow the steps below.</span>
          </div>
        </div>
      </Card>

      {/* Licence readiness */}
      {!subLoading && !tokensLoading && (
        <div className={`${styles.statusBar} ${hasActivePlan ? styles.statusOk : styles.statusWarn}`}>
          <div className={styles.statusIcon}>
            {hasActivePlan ? <CheckCircleIcon size={20} /> : <AlertCircleIcon size={20} />}
          </div>
          <div className={styles.statusText}>
            {hasActivePlan ? (
              <>
                <strong>
                  {planName} is active · {availableLicences} of {totalLicences} licence
                  {totalLicences === 1 ? '' : 's'} available for activation
                </strong>
                <span>
                  {availableLicences > 0
                    ? 'Generate one token for each computer you install the app on.'
                    : 'All licences are in use. Buy more licences to activate another computer.'}
                </span>
              </>
            ) : (
              <>
                <strong>You need an active plan to activate the app</strong>
                <span>Choose a plan (or use your free trial) before generating a licence token.</span>
              </>
            )}
          </div>
          {hasActivePlan ? (
            availableLicences > 0 ? (
              <Button leftIcon={<KeyIcon />} onClick={goToTokens}>Generate Token</Button>
            ) : (
              <Button onClick={() => navigate('/plans')}>Add Licences</Button>
            )
          ) : (
            <Button onClick={() => navigate('/plans')}>View Plans</Button>
          )}
        </div>
      )}

      <div className={styles.grid}>
        {/* Setup steps */}
        <Card padding="none" className={styles.stepsCard}>
          <div className={styles.cardInner}>
            <h3 className={styles.sectionTitle}>How to set up</h3>
            <ol className={styles.steps}>
              {STEPS.map((step, index) => (
                <li key={step.title} className={styles.step}>
                  <span className={styles.stepNumber}>{index + 1}</span>
                  <div className={styles.stepContent}>
                    <h4 className={styles.stepTitle}>{step.title}</h4>
                    <p className={styles.stepBody}>{step.body}</p>
                    {step.action === 'token' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        leftIcon={<KeyIcon />}
                        onClick={goToTokens}
                        className={styles.stepButton}
                      >
                        Go to License Tokens
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Card>

        <div className={styles.sideColumn}>
          {/* Requirements */}
          <Card padding="none">
            <div className={styles.cardInner}>
              <h3 className={styles.sectionTitle}>
                <ShieldIcon size={16} /> Before you start
              </h3>
              <ul className={styles.checkList}>
                {REQUIREMENTS.map(item => (
                  <li key={item}>
                    <CheckCircleIcon size={16} className={styles.checkIcon} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>

          {/* Token tips */}
          <Card padding="none">
            <div className={styles.cardInner}>
              <h3 className={styles.sectionTitle}>
                <InfoIcon size={16} /> About licence tokens
              </h3>
              <ul className={styles.tipList}>
                <li>One token activates the app on <strong>one computer</strong>.</li>
                <li>Keep the token private — anyone with it can use your licence.</li>
                <li>Installing on another computer? Generate a new token for it.</li>
                <li>Your number of tokens depends on the licences in your plan.</li>
              </ul>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Download;
